import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import React from 'react';

import { SHARE_PERMISSION_FIELDS, SharePermissionField } from './shareAccessHelpers';
import { ColorProps, ReportAccessData } from '../../../../../@types';
import {
  FormControlLabel,
  FormControlLabelPlacementProps,
} from '../../../../../components/atoms/FormControlLabel';
import { Switch } from '../../../../../components/atoms/Switch';

/** Matches CELL_SX on the My Reports table this dialog opens from. */
const CELL_SX = {
  fontSize: '12px',
  py: 0.5,
  px: 1,
  borderBottom: '1px solid',
  borderColor: 'v4.border',
} as const;

export interface ShareAccessTableRowProps {
  row: ReportAccessData;
  even: boolean;
  onTogglePermission: (field: SharePermissionField, checked: boolean) => void;
  onRemove: () => void;
}

export const ShareAccessTableRow: React.FC<ShareAccessTableRowProps> = ({
  row,
  even,
  onTogglePermission,
  onRemove,
}) => {
  const name = row.name || 'this recipient';

  return (
    <TableRow
      sx={{
        bgcolor: even ? 'v4.tableEven' : 'transparent',
        '&:hover': { bgcolor: 'v4.tableHover' },
      }}
    >
      <TableCell sx={CELL_SX}>
        {/* `name`, not `row.name`: a record with no name would render an empty
            cell while its switches announced a fallback the user cannot see. */}
        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{name}</Typography>
      </TableCell>
      <TableCell sx={CELL_SX}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {SHARE_PERMISSION_FIELDS.map(({ field, label }) => (
            <FormControlLabel
              key={field}
              // `End` placement keeps rows compact; the previous `Top` placement
              // stacked a caption above every switch and made rows ~70px tall.
              labelPlacement={FormControlLabelPlacementProps.End}
              sx={{ m: 0 }}
              control={
                <Switch
                  color={ColorProps.Primary}
                  checked={Boolean(row[field])}
                  // Named per row: three identically-labelled switches per row
                  // are otherwise indistinguishable to a screen reader.
                  ariaLabel={`${label} for ${name}`}
                  onChange={(event) => onTogglePermission(field, event.target.checked)}
                />
              }
              label={<Typography variant="caption">{label}</Typography>}
            />
          ))}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ ...CELL_SX, width: 48 }}>
        {row.isActive && (
          <IconButton
            size="small"
            aria-label={`Remove ${name} from this report`}
            onClick={onRemove}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'error.main', bgcolor: 'v4.tableHover' },
            }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
};
